/**
 * PATH: packages/ui/src/kanban/components/KanbanCardFooterChrome.tsx
 * FILE_ID: UI-KANBAN-CARD-FOOTER-CHROME-001
 * PURPOSE: Right-side footer chrome — job chip, mailbox, assignees, kebab.
 * ROLE IN ARCHITECTURE: Presentational leaf under KanbanCardFooter.
 * MAIN EXPORTS: KanbanCardFooterChrome
 * NON-RESPONSIBILITIES: Does not own the left triage strip.
 * NOTES FOR FUTURE AI: Hide omits chrome. Compact still paints trailingSlot.
 */
import type { ReactNode } from 'react'

import type { KanbanEmailCardMeta, KanbanMember } from '../api'
import type { Card as KanbanCardModel } from '../types'
import { readableForeground } from '../utils'
import { KanbanAvatarStack } from './KanbanAvatar'
import { footerFieldOn, type FooterFieldVisibility } from './kanbanCardFooterSignals'
import { cardReferenceChip, getMailboxInitials, pickHashedColor } from './kanbanCardVisuals'

export function KanbanCardFooterChrome({
  card,
  isCompact,
  emailMeta,
  assignedMembers,
  trailingSlot,
  fields,
}: {
  card: KanbanCardModel
  isCompact: boolean
  emailMeta: KanbanEmailCardMeta | undefined
  assignedMembers: KanbanMember[]
  trailingSlot?: ReactNode
  fields?: FooterFieldVisibility
}) {
  if (isCompact && !trailingSlot) return null
  const on = (id: Parameters<typeof footerFieldOn>[1]) => footerFieldOn(fields, id)
  const ref = !isCompact && on('jobRef') ? cardReferenceChip(card) : null
  const mailbox = !isCompact && on('mailbox') ? emailMeta?.mailbox_address : undefined
  const people = !isCompact && on('assignees') ? assignedMembers : []

  return (
    <div
      data-card-footer-chrome=""
      className="flex items-center gap-1.5 shrink-0"
    >
      {ref && (
        <span
          className="inline-flex items-center rounded-full border border-neutral-300 px-1.5 py-px text-[10px] font-medium leading-4 text-neutral-500 dark:border-zinc-700 dark:text-zinc-400"
          title={ref.title}
          aria-label={ref.title}
          data-testid="kanban-card-reference-chip"
        >
          {ref.label}
        </span>
      )}
      {mailbox &&
        (() => {
          const mbColor = pickHashedColor(`mb:${mailbox}`)
          const fg = readableForeground(mbColor)
          return (
            <span
              className="inline-flex h-4 w-4 shrink-0 items-center justify-center rounded-full text-[8px] font-bold leading-none ring-1 ring-white/40 dark:ring-zinc-900/40"
              style={{ background: mbColor, color: fg }}
              title={`Mailbox / team inbox: ${mailbox}`}
              aria-label={`Mailbox ${mailbox}`}
            >
              {getMailboxInitials(mailbox)}
            </span>
          )
        })()}
      {people.length > 0 && <KanbanAvatarStack members={people} max={3} size={18} />}
      {trailingSlot}
    </div>
  )
}
